import React, { useState, useEffect } from 'react';
import { Gamepad2, Trophy, Medal, Sparkles, Star, Plus, Edit, Trash2, Save } from 'lucide-react';
import AdminLayout from '../../components/admin/AdminLayout';
import AdminTable from '../../components/admin/AdminTable';
import AdminCard from '../../components/admin/AdminCard';
import AdminModal from '../../components/admin/AdminModal';
import api from '../../services/api';
import { useProgram } from '../../contexts/ProgramContext';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import '../../styles/pages/admin/GamificationPage.css';
import '../../styles/admin-shared.css';

const GamificationPage = () => {
  // Re-fetch on program switch
  const { slug } = useProgram();
  const [achievements, setAchievements] = useState([]);
  const [badges, setBadges] = useState([]);
  const [stats, setStats] = useState({
    totalAchievements: 0,
    totalBadges: 0,
    totalUnlocked: 0,
    avgPoints: 0
  });
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [modalType, setModalType] = useState('achievement'); // 'achievement' or 'badge'
  const [activeTab, setActiveTab] = useState('achievements');
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    icon: '',
    category: 'PROGRESS',
    requirement: 1,
    points: 10,
    rarity: 'COMMON',
    unlock_condition: ''
  });

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [achievementsData, badgesData, statsData] = await Promise.all([
        api.get('/admin/gamification/achievements').catch(() => ({ data: { data: { achievements: [] } } })),
        api.get('/admin/gamification/badges').catch(() => ({ data: { data: { badges: [] } } })),
        api.get('/admin/gamification/stats').catch(() => ({ data: { data: {} } }))
      ]);

      setAchievements(achievementsData.data.data?.achievements || []);
      setBadges(badgesData.data.data?.badges || []);
      setStats(statsData.data.data || {
        totalAchievements: 0,
        totalBadges: 0,
        totalUnlocked: 0,
        avgPoints: 0
      });
    } catch (error) {
      console.error('Erro ao carregar gamificação:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (type, item = null) => {
    setModalType(type);
    
    if (item) {
      setEditing(item);
      if (type === 'achievement') {
        setFormData({
          name: item.name,
          description: item.description || '',
          icon: item.icon || '',
          category: item.category || 'PROGRESS',
          requirement: item.requirement || 1,
          points: item.points || 10,
          rarity: 'COMMON',
          unlock_condition: ''
        });
      } else {
        setFormData({
          name: item.name,
          description: item.description || '',
          icon: item.icon || '',
          category: 'PROGRESS',
          requirement: 1,
          points: 10,
          rarity: item.rarity || 'COMMON',
          unlock_condition: item.unlock_condition || ''
        });
      }
    } else {
      setEditing(null);
      setFormData({
        name: '',
        description: '',
        icon: '',
        category: 'PROGRESS',
        requirement: 1,
        points: 10,
        rarity: 'COMMON',
        unlock_condition: ''
      });
    }
    setShowModal(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      const endpoint = modalType === 'achievement' ? 'achievements' : 'badges';
      const data = {
        ...formData,
        requirement: parseInt(formData.requirement),
        points: parseInt(formData.points)
      };

      if (editing) {
        await api.put(`/admin/gamification/${endpoint}/${editing.id}`, data);
      } else {
        await api.post(`/admin/gamification/${endpoint}`, data);
      }
      
      setShowModal(false);
      setEditing(null);
      fetchData();
    } catch (error) {
      console.error('Erro ao salvar:', error);
      alert(error.response?.data?.message || 'Erro ao salvar');
    }
  };

  const handleDelete = async (type, id) => {
    if (!window.confirm(`Deseja realmente excluir este ${type === 'achievement' ? 'achievement' : 'badge'}?`)) return;
    
    try {
      const endpoint = type === 'achievement' ? 'achievements' : 'badges';
      await api.delete(`/admin/gamification/${endpoint}/${id}`);
      fetchData();
    } catch (error) {
      console.error('Erro ao excluir:', error);
      alert(error.response?.data?.message || 'Erro ao excluir');
    }
  };

  const achievementColumns = [
    {
      key: 'name',
      label: 'Achievement',
      render: (value, row) => (
        <div className="achievement-cell">
          <span className="achievement-icon">{row.icon}</span>
          <div>
            <div className="achievement-name">{value}</div>
            <div className="achievement-category">{row.category}</div>
          </div>
        </div>
      )
    },
    {
      key: 'description',
      label: 'Descrição',
      render: (value) => value?.substring(0, 60) + '...' || 'Sem descrição'
    },
    {
      key: 'requirement',
      label: 'Requisito',
      render: (value) => `${value} ações`
    },
    {
      key: 'points',
      label: 'Pontos',
      render: (value) => (
        <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <Star size={16} className="text-warning-500" /> {value}
        </span>
      )
    },
    {
      key: 'unlocked_count',
      label: 'Desbloqueados',
      render: (value) => `${value || 0} usuários`
    }
  ];

  const badgeColumns = [
    {
      key: 'name',
      label: 'Badge',
      render: (value, row) => (
        <div className="badge-cell">
          <span className="badge-icon">{row.icon}</span>
          <div>
            <div className="badge-name">{value}</div>
            <div className="badge-rarity">{row.rarity}</div>
          </div>
        </div>
      )
    },
    {
      key: 'description',
      label: 'Descrição',
      render: (value) => value?.substring(0, 60) + '...' || 'Sem descrição'
    },
    {
      key: 'unlock_condition',
      label: 'Condição',
      render: (value) => value || 'Não definida'
    },
    {
      key: 'rarity',
      label: 'Raridade',
      render: (value) => {
        const rarityMap = {
          COMMON: { label: 'Comum', class: 'info' },
          RARE: { label: 'Raro', class: 'primary' },
          EPIC: { label: 'Épico', class: 'warning' },
          LEGENDARY: { label: 'Lendário', class: 'danger' }
        };
        const rarity = rarityMap[value] || { label: value, class: 'default' };
        return <span className={`badge badge-${rarity.class}`}>{rarity.label}</span>;
      }
    },
    {
      key: 'unlocked_count',
      label: 'Desbloqueados',
      render: (value) => `${value || 0} usuários`
    }
  ];

  return (
    <AdminLayout>
      <div className="gamification-page">
        <div className="page-header">
          <div>
            <h1 style={{ display: 'flex', alignItems: 'center', gap: '12px' }}><Gamepad2 size={32} /> Gamificação</h1>
            <p>Gerencie achievements, badges e sistema de recompensas</p>
          </div>
          <button 
            className="btn btn-primary"
            style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
            onClick={() => handleOpenModal(activeTab === 'achievements' ? 'achievement' : 'badge')}
          >
            <Plus size={18} /> Criar {activeTab === 'achievements' ? 'Achievement' : 'Badge'}
          </button>
        </div>

        <div className="analytics-cards-grid">
          <AdminCard
            title="Total de Achievements"
            value={stats.totalAchievements || 0}
            subtitle="Conquistas disponíveis"
            icon={<Trophy size={24} />}
            color="yellow"
          />
          <AdminCard
            title="Total de Badges"
            value={stats.totalBadges || 0}
            subtitle="Badges criados"
            icon={<Medal size={24} />}
            color="purple"
          />
          <AdminCard
            title="Total Desbloqueado"
            value={stats.totalUnlocked || 0}
            subtitle="Por todos os usuários"
            icon={<Sparkles size={24} />}
            color="green"
          />
          <AdminCard
            title="Média de Pontos"
            value={stats.avgPoints || 0}
            subtitle="Por usuário"
            icon={<Star size={24} />}
            color="blue"
          />
        </div>

        <div className="tabs-container">
          <button 
            className={`tab-btn ${activeTab === 'achievements' ? 'active' : ''}`}
            onClick={() => setActiveTab('achievements')}
            style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
          >
            <Trophy size={18} /> Achievements ({achievements.length})
          </button>
          <button 
            className={`tab-btn ${activeTab === 'badges' ? 'active' : ''}`}
            onClick={() => setActiveTab('badges')}
            style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
          >
            <Medal size={18} /> Badges ({badges.length})
          </button>
        </div>

        {activeTab === 'achievements' && (
          <div className="table-section">
            <AdminTable
              columns={achievementColumns}
              data={achievements}
              loading={loading}
              actions={(row) => (
                <div className="table-actions">
                  <button 
                    className="table-action-btn edit"
                    onClick={() => handleOpenModal('achievement', row)}
                    title="Editar achievement"
                  >
                    <Edit size={16} /> Editar
                  </button>
                  <button 
                    className="table-action-btn delete"
                    onClick={() => handleDelete('achievement', row.id)}
                    title="Excluir achievement"
                  >
                    <Trash2 size={16} /> Excluir
                  </button>
                </div>
              )}
            />
          </div>
        )}

        {activeTab === 'badges' && (
          <div className="table-section">
            <AdminTable
              columns={badgeColumns}
              data={badges}
              loading={loading}
              actions={(row) => (
                <div className="table-actions">
                  <button 
                    className="table-action-btn edit"
                    onClick={() => handleOpenModal('badge', row)}
                    title="Editar badge"
                  >
                    <Edit size={16} /> Editar
                  </button>
                  <button 
                    className="table-action-btn delete"
                    onClick={() => handleDelete('badge', row.id)}
                    title="Excluir badge"
                  >
                    <Trash2 size={16} /> Excluir
                  </button>
                </div>
              )}
            />
          </div>
        )}

        <AdminModal
          isOpen={showModal}
          onClose={() => {
            setShowModal(false);
            setEditing(null);
          }}
          title={
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              {editing ? <Edit size={24} /> : <Plus size={24} />}
              {editing ? 'Editar' : 'Criar'} {modalType === 'achievement' ? 'Achievement' : 'Badge'}
            </div>
          }
        >
          <form onSubmit={handleSave} className="gamification-form">
            <div className="form-group">
              <label htmlFor="name">Nome *</label>
              <input
                type="text"
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder={`Ex: ${modalType === 'achievement' ? 'Primeira Vitória' : 'Warrior'}`}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="description">Descrição *</label>
              <textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Descreva como desbloquear..."
                rows="3"
                required
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="icon">Ícone</label>
                <input
                  type="text"
                  id="icon"
                  value={formData.icon}
                  onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                  placeholder={modalType === 'achievement' ? 'Ex: 🏆' : 'Ex: 🎖️'}
                  maxLength="5"
                />
              </div>

              {modalType === 'achievement' ? (
                <div className="form-group">
                  <label htmlFor="category">Categoria *</label>
                  <select
                    id="category"
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    required
                  >
                    <option value="PROGRESS">Progresso</option>
                    <option value="SOCIAL">Social</option>
                    <option value="CHALLENGE">Desafio</option>
                    <option value="STREAK">Sequência</option>
                    <option value="SPECIAL">Especial</option>
                  </select>
                </div>
              ) : (
                <div className="form-group">
                  <label htmlFor="rarity">Raridade *</label>
                  <select
                    id="rarity"
                    value={formData.rarity}
                    onChange={(e) => setFormData({ ...formData, rarity: e.target.value })}
                    required
                  >
                    <option value="COMMON">Comum</option>
                    <option value="RARE">Raro</option>
                    <option value="EPIC">Épico</option>
                    <option value="LEGENDARY">Lendário</option>
                  </select>
                </div>
              )}
            </div>

            {modalType === 'achievement' ? (
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="requirement">Requisito *</label>
                  <input
                    type="number"
                    id="requirement"
                    value={formData.requirement}
                    onChange={(e) => setFormData({ ...formData, requirement: e.target.value })}
                    min="1"
                    required
                  />
                  <small className="form-hint">Quantidade de ações necessárias</small>
                </div>

                <div className="form-group">
                  <label htmlFor="points">Pontos *</label>
                  <input
                    type="number"
                    id="points"
                    value={formData.points}
                    onChange={(e) => setFormData({ ...formData, points: e.target.value })}
                    min="1"
                    required
                  />
                  <small className="form-hint">Pontos ganhos ao desbloquear</small>
                </div>
              </div>
            ) : (
              <div className="form-group">
                <label htmlFor="unlock_condition">Condição de Desbloqueio *</label>
                <input
                  type="text"
                  id="unlock_condition"
                  value={formData.unlock_condition}
                  onChange={(e) => setFormData({ ...formData, unlock_condition: e.target.value })}
                  placeholder="Ex: 30_days_clean, complete_10_challenges"
                  required
                />
                <small className="form-hint">Código da condição para verificação</small>
              </div>
            )}

            <div className="modal-actions">
              <button 
                type="button" 
                className="btn btn-outline"
                onClick={() => setShowModal(false)}
              >
                Cancelar
              </button>
              <button type="submit" className="btn btn-primary" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                {editing ? <><Save size={18} /> Salvar Alterações</> : <><Plus size={18} /> Criar</>}
              </button>
            </div>
          </form>
        </AdminModal>
      </div>
    </AdminLayout>
  );
};

export default GamificationPage;
